import React, { useRef, useEffect, useState } from "react";
import Color from "color";
import { useGlobalValue } from "../../../state/GlobalProvider";
import { format, getPropertyValue, getValueFromJson } from "../../../utils";
import styles from "./BarChart.module.sass";
import NoResults from "../../NoResults/NoResults";
import { createBarChartCustomTooltip } from "./createBarChartCustomTooltip";
import { setParsedCurrency } from "../../../utils/format";
const TICKS_FONT_SIZE = 12;
const modifiers = ["asc", "desc", "none"];
const replaceLabelPlaceholders = (labelText, values) => {
  let result = labelText;
  for (const key in values) {
    const placeholder = `{${key}}`;
    result = result.split(placeholder).join(values[key]);
  }
  return result;
};
function transformSumData(input) {
  const maxLength = Math.max(...input.map(item => item.data.length));
  const resultData = Array(maxLength).fill(0);
  input.forEach(item => {
    item.data.forEach((value, index) => {
      resultData[index] += value;
    });
  });
  return {
    data: resultData
  };
}
const composeSummarizedData = data => {
  let minPeriod = 1;
  let maxPeriod = 12;
  const summarized = data.reduce(([legend, summaryData], item, period) => {
    minPeriod = Math.min(minPeriod, period);
    maxPeriod = Math.max(maxPeriod, period);
    const summary = item.summary || [];
    summary.forEach(({
      key,
      value
    }) => {
      let index = summaryData.findIndex(([elem]) => elem?.key === key);
      index = index >= 0 ? index : summaryData.length;
      if (!summaryData[index]) {
        summaryData[index] = [];
      }
      summaryData[index].push({
        key,
        period,
        value,
        columnName: item.period || period
      });
      if (!legend.includes(key)) {
        legend.push(key);
      }
    });
    return [legend, summaryData];
  }, [[], []]);
  return [summarized[0], summarized[1].map(dataset => {
    let result = [];
    for (let i = minPeriod, l = maxPeriod; i <= l; i++) {
      const entry = dataset.find(elem => elem?.period === i);
      if (entry) {
        result.push({
          category: entry.key,
          period: i,
          value: entry.value,
          columnName: entry.columnName
        });
      } else if (data[i] && data[i].summary !== null) {
        result.push({
          category: undefined,
          period: i,
          value: 0,
          columnName: undefined
        });
      }
    }
    return result;
    // return dataset.map(({ key, period, value }) => ({ category: key, period, value }))
  })];
};
const transform = (data, summarized = false, dataLabels, organizationGraphChartColors, chartColors, hoverOrganizationChartColors, isOrganizationGraph) => {
  const labels = [];
  const columnName = {};
  if (summarized) {
    const [legend, summaryData] = composeSummarizedData(data);
    data = summaryData;
    dataLabels = dataLabels || legend;
  }
  chartColors.forEach((color, index) => {
    const secondaryColor = new Color(color);
    const sFade = secondaryColor.contrast(Color("white")) > 3 ? "lighten" : "darken";
    const newColor = secondaryColor[sFade](0.2 * (index + 1)).hex();
    if (!chartColors.includes(newColor)) {
      chartColors.push(newColor);
    }
  });
  const datasets = data.map((dataset, dsIndex) => {
    if (!dataset) {
      return [];
    }
    const datasetValues = [];
    let dataCategory;
    if (Array.isArray(dataset)) {
      dataset.forEach(({
        value,
        category,
        ...rest
      }) => {
        const label = Object.entries(rest)[0][0];
        const labelValue = `${rest[label]}`;
        if (!labels.includes(labelValue)) {
          labels.push(labelValue);
        }
        if (summarized) {
          // TODO: Check if this makes sense -> To talk with Joao Ribeiro (Ruben will do it maybe)
          if (rest["columnName"]) {
            columnName[rest["period"]] = `${rest["columnName"]}`;
          } else if (!columnName[rest["period"]]) {
            columnName[rest["period"]] = `${rest["period"]}`;
          }
        }
        if (!dataCategory) {
          dataCategory = category;
        }
        datasetValues.push(value);
      });
    }
    return {
      label: (dataLabels || [])[dsIndex],
      data: datasetValues,
      dataCategory,
      backgroundColor: isOrganizationGraph ? organizationGraphChartColors[dsIndex] : chartColors[dsIndex],
      hoverBackgroundColor: hoverOrganizationChartColors[dsIndex]
    };
  });
  return {
    labels: Object.keys(columnName)?.length ? Object.values(columnName) : labels,
    datasets
  };
};
function isArray(value) {
  if (typeof value === "string") {
    try {
      return Array.isArray(JSON.parse(value));
    } catch (e) {
      return false;
    }
  } else {
    return Array.isArray(value);
  }
}
function parseOrderString(orderString) {
  const orderMatch = orderString.split("|");
  if (!orderMatch) {
    thrownewError("Invalid order string format");
  }
  let modifier = "asc";
  let customOrder = [];
  let orderBy = "label";
  orderMatch.map(current => {
    // valid array
    if (isArray(current)) {
      customOrder = JSON.parse(current);
    } else if (current.length && current[0] === "(" && current[current.length - 1] === ")") {
      modifier = current.replace("(", "").replace(")", "");
      modifier = modifiers.includes(modifier) ? modifier : "asc";
    } else if (current && orderMatch.length === 3) {
      orderBy = current;
    }
  });
  return {
    modifier,
    customOrder,
    orderBy
  };
}
function createSortFunction({
  customOrder,
  orderBy,
  modifier
}) {
  return (a, b) => {
    if (customOrder.length > 0) {
      const indexA = customOrder.indexOf(a[orderBy]);
      const indexB = customOrder.indexOf(b[orderBy]);
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      } else if (indexA !== -1) {
        return -1;
      } else if (indexB !== -1) {
        return 1;
      }
    }
    return a[orderBy] > b[orderBy] ? 1 : a[orderBy] < b[orderBy] ? -1 : 0;
  };
}
const renderChart = (canvas, data, summarized, dataLabels, labelsPosition, hasCurrency, stacked, links, colors, organizationGraphChartColors, chartColors, hoverOrganizationChartColors, goto, params, i18n, currencyType, isOrganizationGraph, currency, customTooltipLabel, labelsType, labelsOrder) => {
  let chartData = null;
  const [legend] = composeSummarizedData(data);
  dataLabels = dataLabels || legend;
  const style = getComputedStyle(canvas);
  const ctx = canvas.getContext("2d");
  const chartTextColor = labelsPosition !== "top" ? getPropertyValue("--chart-text-color").trim() : getPropertyValue("--chart-text-top-color").trim();
  chartData = data ? transform(data, summarized, dataLabels, organizationGraphChartColors, chartColors, hoverOrganizationChartColors, isOrganizationGraph) : null;
  let jsonOrder = null;
  if (labelsOrder) {
    jsonOrder = parseOrderString(labelsOrder);
    chartData.datasets = jsonOrder.modifier === "desc" ? chartData.datasets.sort(createSortFunction(jsonOrder)).reverse() : chartData.datasets.sort(createSortFunction(jsonOrder));
  }
  const chart = new Chart(ctx, {
    type: "bar",
    data: chartData,
    options: {
      showTooltips: true,
      layout: {
        padding: {
          left: -14,
          top: labelsPosition === "top" && labelsType === "normal" ? 10 + 12 * Math.max(1, (chartData.datasets || []).length) : 24
        }
      },
      hover: {
        mode: "nearest",
        animationDuration: 0,
        onHover: (e, chartElements) => {
          e.target.style.cursor = chartElements[0] ? "pointer" : "default";
        }
      },
      onClick: evt => {
        const activePoints = chart.getElementAtEvent(evt);
        const tooltipItem = activePoints[0] || {};
        const link = (links || [])[tooltipItem._datasetIndex] || (links || [])[0];
        const filterValue = tooltipItem._model && tooltipItem._model.label;
        if (goto && link && Object.keys(link).length && filterValue) {
          const dataset = chart.config.data.datasets[tooltipItem._datasetIndex] || {
            data: []
          };
          const category = dataset.dataCategory;
          setTimeout(() => {
            const option = ((link || {}).options || {})[params.entity];
            let oldTooltipEl = document.getElementById("chartjs-tooltip");
            if (oldTooltipEl) {
              document.body.removeChild(oldTooltipEl);
            }
            goto(link.to, [...(link.filter || []), {
              column: link.filterColumn,
              filters: [{
                value: filterValue
              }],
              operator: link.operator
            }, category && link.category ? {
              column: link.category,
              filters: [{
                value: category
              }]
            } : category && option ? {
              column: option,
              filters: [{
                value: category
              }]
            } : undefined].filter(f => f));
          });
        }
      },
      tooltips: {
        enabled: false,
        custom: createBarChartCustomTooltip(isOrganizationGraph, organizationGraphChartColors, chartColors, dataLabels, i18n),
        callbacks: {
          title: function (tooltipItem, data) {
            const dataset = data.datasets[tooltipItem[0].datasetIndex] || {
              labels: []
            };
            let labelValues = {
              category: dataset?.dataCategory || "",
              dataLabel: null,
              value: format(tooltipItem?.[0]?.yLabel, hasCurrency ? "short" : undefined, hasCurrency ? currency.currency : null, hasCurrency ? getValueFromJson(currencyType, "fractionDigits") || 2 : 0, currency.locale)
            };
            if (!summarized && dataset.label) {
              const dataLabel = (data.labels || [])[tooltipItem[0].index] || "";
              labelValues.dataLabel = dataLabel;
              return customTooltipLabel ? replaceLabelPlaceholders(customTooltipLabel, labelValues) : i18n`${dataLabel}th_day_of_month`;
            }
            const dataLabel = (data.labels || [])[tooltipItem[0].index] ? `${dataset.dataCategory ? `${(data.labels || [])[tooltipItem[0].index] || ""}` : (data.labels || [])[tooltipItem[0].index]}` : "";
            labelValues.dataLabel = dataLabel;
            return customTooltipLabel ? replaceLabelPlaceholders(customTooltipLabel, labelValues) : i18n`${dataLabel}th_day_of_month`;
          },
          label: function (tooltipItem) {
            return format(tooltipItem.yLabel, hasCurrency ? "short" : undefined, hasCurrency ? currency.currency : null, hasCurrency ? getValueFromJson(currencyType, "fractionDigits") || 2 : 0, currency.locale);
          }
        },
        displayColors: false,
        backgroundColor: colors.$chartTooltipBackgroundColor,
        borderColor: colors.$chartTooltipBorderColor,
        borderWidth: 1,
        cornerRadius: 0
      },
      animation: {
        duration: 0
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: jsonOrder && jsonOrder.modifier === "none" ? false : true,
        position: "bottom",
        onClick: e => e.stopPropagation(),
        labels: {
          fontFamily: style.fontFamily,
          fontSize: 10,
          fontColor: colors.$chartSecondaryColor,
          boxHeight: 4,
          boxWidth: 6,
          generateLabels: chart => {
            var labels = Chart.defaults.global.legend.labels.generateLabels(chart);
            labels?.forEach(label => {
              // Didn't found any way to manipulate this with css
              label.text = i18n`${label.text}             `;
            });
            return labels;
          }
        }
      },
      scales: {
        xAxes: [{
          stacked: stacked,
          gridLines: {
            display: false,
            drawBorder: false
          },
          categoryPercentage: 0.8,
          barPercentage: 0.5 + Math.min(0.3, 0.1 * (chartData.datasets || []).length),
          maxBarThickness: 80,
          ticks: {
            beginAtZero: true,
            fontStyle: "bold",
            padding: 13,
            fontColor: style.color
          }
        }],
        yAxes: [{
          stacked: stacked,
          gridLines: {
            display: isOrganizationGraph ? false : true,
            drawBorder: false,
            color: style.borderColor,
            zeroLineColor: style.borderColor
          },
          ticks: {
            beginAtZero: true,
            padding: 8,
            fontStyle: "normal",
            fontColor: style.fill,
            fontSize: TICKS_FONT_SIZE,
            callback: function (label, index, labels) {
              return index === 0 || label === 0 || index === labels.length - 1 ? "" : null;
            }
          }
        }]
      },
      plugins: {
        chartJsPluginBarChartBackground: {
          color: getPropertyValue("--chart-background-color-accounting"),
          backgroundOffset: 6
        }
      }
    },
    plugins: [{
      afterDatasetsDraw: function (chartInstance) {
        const innerCtx = chartInstance.ctx;
        innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
        innerCtx.save();
        innerCtx.textAlign = "center";
        innerCtx.textBaseline = "bottom";
        const stacked = (chartInstance.config.options.scales.xAxes.find(({
          stacked
        }) => stacked) || {}).stacked;
        const topOffset = stacked ? 10 + 10 * chartInstance.config.data.datasets.length : 20;
        chartInstance.config.data.datasets.forEach((dataset, i) => {
          innerCtx.fillStyle = chartTextColor;
          const meta = chartInstance.controller.getDatasetMeta(i);
          const yOffset = topOffset + 10 * i * (stacked ? -1 : 1);
          if (labelsType === "normal" || labelsType === "sum" && chartInstance.config.data.datasets.length === i + 1) {
            meta.data.forEach(function (bar, index) {
              const x = bar._view.x;
              let currentData = dataset;
              if (labelsType === "sum") {
                currentData = transformSumData(chartInstance.config.data.datasets);
              }
              const data = currentData ? currentData.data[index] : 0;
              if (data) {
                const formatted = format(data, hasCurrency ? "short" : undefined, hasCurrency ? currency.currency : null, hasCurrency ? getValueFromJson(currencyType, "fractionDigits") || 2 : 0, currency.locale);
                if (labelsPosition === "top") {
                  innerCtx.fillText(formatted, x, yOffset);
                } else {
                  innerCtx.fillText(formatted, bar._model.x, bar._model.y + (bar._model.base - bar._model.y) / 2 + 5);
                }
              }
            });
          }
        });
        innerCtx.restore();
      }
    }]
  });
  return chart;
};
const BarChart = props => {
  const {
    theme,
    goto,
    params,
    i18n
  } = useGlobalValue();
  const {
    data,
    summarized = false,
    dataLabels,
    labelsPosition = "top",
    labelsType = "normal",
    labelsOrder,
    links,
    stacked,
    currency: hasCurrency = false,
    modifiers,
    currencyType,
    isOrganizationGraph,
    dynamicCurrency,
    customTooltipLabel
  } = props;
  const [currency, setCurrency] = useState({
    locale: undefined,
    currency: false
  });
  const canvas = useRef(null);
  const {
    colors = {}
  } = theme || {};
  const organizationGraphChartColors = [getPropertyValue("--pie-chart-sixth-color-cyan").trim(), getPropertyValue("--pie-chart-fourth-color-orange").trim(), getPropertyValue("--chart-background-color-organizations-graph").trim(), getPropertyValue("--sovos-data-blue-50").trim(), getPropertyValue("--sovos-data-purple-90").trim(), getPropertyValue("--sovos-data-red-90").trim(), getPropertyValue("--sovos-data-red-60").trim(), getPropertyValue("--sovos-data-cyan-80").trim(), getPropertyValue("--sovos-data-blue-70").trim(), getPropertyValue("--sovos-data-green-60").trim(), getPropertyValue("--sovos-data-cyan-90").trim(), getPropertyValue("--sovos-data-cyan-60").trim(), getPropertyValue("--sovos-data-orange-90").trim(), getPropertyValue("--sovos-data-yellow-70").trim(), getPropertyValue("--sovos-data-orange-40").trim(), getPropertyValue("--sovos-data-red-70").trim(), getPropertyValue("--sovos-data-yellow-50").trim(), getPropertyValue("--sovos-data-blue-80").trim()];
  const hoverOrganizationChartColors = [getPropertyValue("--pie-chart-eigth-color-cyan").trim(), getPropertyValue("--pie-chart-sixth-color-orange").trim(), getPropertyValue("--sovos-data-blue-50").trim(), getPropertyValue("--sovos-data-purple-90").trim(), getPropertyValue("--sovos-data-red-90").trim(), getPropertyValue("--sovos-data-red-60").trim(), getPropertyValue("--sovos-data-cyan-80").trim(), getPropertyValue("--sovos-data-blue-70").trim(), getPropertyValue("--sovos-data-green-60").trim(), getPropertyValue("--sovos-data-cyan-90").trim(), getPropertyValue("--sovos-data-cyan-60").trim(), getPropertyValue("--sovos-data-orange-90").trim(), getPropertyValue("--sovos-data-yellow-70").trim(), getPropertyValue("--sovos-data-orange-40").trim(), getPropertyValue("--sovos-data-red-70").trim(), getPropertyValue("--sovos-data-yellow-50").trim(), getPropertyValue("--sovos-data-blue-80").trim()];
  const chartColors = [getPropertyValue("--pie-chart-sixth-color-cyan").trim(), getPropertyValue("--pie-chart-fourth-color-orange").trim(), getPropertyValue("--sovos-data-blue-50").trim(), getPropertyValue("--sovos-data-purple-90").trim(), getPropertyValue("--sovos-data-red-90").trim(), getPropertyValue("--sovos-data-red-60").trim(), getPropertyValue("--sovos-data-cyan-80").trim(), getPropertyValue("--sovos-data-blue-70").trim(), getPropertyValue("--sovos-data-green-60").trim(), getPropertyValue("--sovos-data-cyan-90").trim(), getPropertyValue("--sovos-data-cyan-60").trim(), getPropertyValue("--sovos-data-orange-90").trim(), getPropertyValue("--sovos-data-yellow-70").trim(), getPropertyValue("--sovos-data-orange-40").trim(), getPropertyValue("--sovos-data-red-70").trim(), getPropertyValue("--sovos-data-yellow-50").trim(), getPropertyValue("--sovos-data-blue-80").trim()];
  useEffect(() => {
    setParsedCurrency(currencyType, dynamicCurrency, setCurrency);
  }, [dynamicCurrency, currencyType]);
  useEffect(() => {
    const chart = canvas.current && data && renderChart(canvas.current, data || [], summarized, dataLabels, labelsPosition, hasCurrency, stacked, links || [], colors, organizationGraphChartColors, chartColors, hoverOrganizationChartColors, goto, params, i18n, currencyType, isOrganizationGraph, currency, customTooltipLabel, labelsType, labelsOrder);
    return () => {
      chart && chart.destroy();
    };
  }, [canvas, data, colors, currency]);
  const hasResults = isOrganizationGraph && data?.length > 1 || data?.length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: styles["root"]
  }, /*#__PURE__*/React.createElement("div", null, hasResults ? /*#__PURE__*/React.createElement("canvas", {
    ref: canvas,
    className: (modifiers || []).includes("--top-text-small") ? styles["text-small"] : styles[""]
  }) : /*#__PURE__*/React.createElement(NoResults, null)));
};
export default BarChart;